import React, { useCallback, useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import {
  TbChevronUp,
  TbChevronDown,
  TbCheck,
  TbSettings,
} from 'react-icons/tb';

import useRedirect from 'hooks/use-redirect';

import Typography from 'components-new/typography/typography';
import Icon from 'components-new/icon/icon';
import type { DropdownMenuItemType } from 'components-new/dropdown-menu/dropdown-menu-item';
import DropdownMenu from 'components-new/dropdown-menu/dropdown-menu';

import organisationRoutes from 'pages/organization/organisation-routes';

export type OrganisationType = {
  id: number;
  name: string;
  image?: string;
  selected?: boolean;
  onClick: () => void;
};

interface OrganisationSelectProps {
  organisationOptions: OrganisationType[];
}

const OrganisationSelect: React.FC<OrganisationSelectProps> = ({
  organisationOptions,
}) => {
  const redirect = useRedirect();

  const menuButtonClassname = twMerge(
    'h-[40px] w-40 bg-white rounded-full border border-1',
    'px-3 flex justify-between items-center',
    'hover:bg-gray-100 hover:border-gray-300',
    'active:border-primary active:outline active:outline-primary-200 active:bg-gray-100 active:ring-1 active:ring-primary-200 active:ring-offset-1',
    'focus:outline-none focus:ring-1 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200',
    'focus:border-primary',
  );

  const selectedOrganisation = organisationOptions.find(
    (org) => org.selected === true,
  );

  const redirectToOrganisationSettings = useCallback(() => {
    redirect(organisationRoutes.settings.path);
  }, [redirect]);

  const menuButton = (open: boolean) => (
    <button type="button" className={menuButtonClassname}>
      <Typography
        variant="ui-input"
        as="span"
        className="text-gray-500 truncate cursor-pointer ml-2"
      >
        {selectedOrganisation?.name
          ? selectedOrganisation?.name
          : 'Select Organisation'}
      </Typography>
      <Icon
        icon={open ? TbChevronUp : TbChevronDown}
        size={24}
        className="text-gray-500 ml-auto"
      />
    </button>
  );

  const menuItems: DropdownMenuItemType[] = useMemo(() => {
    let items: DropdownMenuItemType[] = organisationOptions.map(
      (organisation, index) => ({
        key: index.toString(),
        label: organisation.name,
        leadingElement: organisation.selected ? (
          <Icon icon={TbCheck} size={20} className="text-primary-300" />
        ) : undefined,
        onClick: organisation.onClick,
      }),
    );

    items = [
      ...items,
      { key: 'divider1', type: 'divider' },
      {
        key: 'organisationSettings',
        label: 'Organisation Settings',
        leadingElement: <Icon icon={TbSettings} size={20} />,
        onClick: redirectToOrganisationSettings,
      },
    ];
    return items;
  }, [organisationOptions, redirectToOrganisationSettings]);

  return (
    <DropdownMenu
      menuButton={menuButton}
      items={menuItems}
      strategy="absolute"
      autoplacement
    />
  );
};

export default OrganisationSelect;
